import { FC, PropsWithChildren } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Icon from './icon';

export interface ModalButton {
  text?: string;
  color?: string;
  icon?: string;
  disabled?: boolean;
  onClick?: () => void;
}

interface AppModalProps extends PropsWithChildren<{}> {
  title?: string | JSX.Element;
  open: boolean;
  toggle(): void;
  size?: string;
  onClosed?: () => void;
  cancelButton?: ModalButton;
  saveButton?: ModalButton;
  unmountOnClose?: boolean;
  backButton?: ModalButton;
  massImportButton?: ModalButton
  customButton?: JSX.Element;
  customSaveButton?: JSX.Element;
  zIndex?: number;
  isTransparentFooter?: boolean,
  isStickyFooter?: boolean
  isDisableCloseBtn?: boolean
};

const defaultCancelButton: ModalButton = {
  text: 'Cancel',
  color: 'outline-secondary'
};

const AppModal: FC<AppModalProps> = (props: AppModalProps) => {
  const { children, title, size = 'md', open, onClosed, toggle, cancelButton = defaultCancelButton, saveButton, unmountOnClose = false, backButton, massImportButton, customButton, customSaveButton = false, zIndex, isTransparentFooter, isStickyFooter, isDisableCloseBtn } = props;
  const footer: HTMLElement | null = document.querySelector('.modal-footer');
  const disableTransparency = () => {
    if (isTransparentFooter && footer) {
      footer.classList.remove("is-transparent");
    }
  }
  const enableTransparency = () => {
    if (isTransparentFooter && footer) {
      footer.classList.add("is-transparent");
    }
  }

  return (
    <Modal
      size={size}
      isOpen={open}
      toggle={toggle}
      unmountOnClose={unmountOnClose}
      zIndex={zIndex}
      onClosed={() => {
        const body = document.body;
        requestAnimationFrame(() => {
          body.style.paddingRight = '';
        });
        !!onClosed && onClosed();
      }}
    >
      <ModalHeader
        toggle={toggle}
        close={(
          <>
            {!isDisableCloseBtn && (
              <div onClick={toggle} className="close">
                <Icon name="close" />
              </div>
            )}
          </>
        )}
      >
        {title}
        {massImportButton && (
          <Button
            color="outline-secondary"
            onClick={massImportButton.onClick}
            className="btn-sm btn-icon mass-import"
          >
            <Icon name="upload" />
            <span>Mass Import</span>
          </Button>
        )}
      </ModalHeader>
      <ModalBody>
        {children}
      </ModalBody>
      <ModalFooter
        onMouseOver={disableTransparency}
        onMouseOut={enableTransparency}
        className={[isStickyFooter && 'is-sticky', isTransparentFooter && 'is-transparent']
          .filter(e => !!e)
          .join('  ')}
      >
        {customButton && (
          <>{customButton}</>
        )}
        {backButton && (
          <Button
            color="link"
            className="btn-icon back-button"
            onClick={backButton.onClick}
          >
            <Icon name="back-button-arrow" />
            <span>Back</span>
          </Button>
        )}
        {cancelButton && (
          <Button
            color={cancelButton.color}
            onClick={cancelButton.onClick ? cancelButton.onClick : toggle}
            key={'cancel-button'}
          >
            {cancelButton.text}
          </Button>
        )}
        {saveButton && (
          <Button
            className={`btn-icon ${saveButton.icon ? 'btn-icon-right' : ''}`.trim()}
            color={saveButton.color}
            onClick={saveButton.onClick}
            disabled={saveButton.disabled}
            key={'save-button'}
          >
            <span>{saveButton.text}</span>
            {saveButton.icon && <Icon name={saveButton.icon} />}
          </Button>
        )}
        {customSaveButton && (
          <>{customSaveButton}</>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AppModal;